import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDateLocale } from './i18n/dateLocale';
import Layout from './components/Layout';
import Login from './pages/Login';
import Home from './pages/Home';
import Products from './pages/Products';
import Clients from './pages/Clients';
import ClientGroups from './pages/ClientGroups';
import NewOrder from './pages/NewOrder';
import Orders from './pages/Orders';
import Users from './pages/Users';
import ChangePassword from './pages/ChangePassword';
import Events from './pages/Events';
import { CircularProgress, Box } from '@mui/material';

// Protected Route component
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, hasRole, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !hasRole(roles)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { isAuthenticated } = useAuth();
  const dateLocale = useDateLocale();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />} 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route 
          path="products" 
          element={
            <ProtectedRoute roles={['admin', 'operator']}>
              <Products />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="clients" 
          element={
            <ProtectedRoute roles={['admin', 'operator']}>
              <Clients />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="client-groups" 
          element={
            <ProtectedRoute roles={['admin']}>
              <ClientGroups />
            </ProtectedRoute>
          } 
        />
        <Route path="new-order" element={<NewOrder />} />
        <Route path="orders" element={<Orders />} />
        <Route 
          path="users" 
          element={
            <ProtectedRoute roles={['admin']}>
              <Users />
            </ProtectedRoute>
          } 
        />
        <Route
          path="events"
          element={
            <ProtectedRoute roles={['admin']}>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route path="change-password" element={<ChangePassword />} />
      </Route>
    </Routes>
    </LocalizationProvider>
  );
}

export default App;
